import type { DocCategory, ServiceCategory } from 'used-vehicle-contracts';
import type { StatusTone } from '../../components/ui/StatusText';

/**
 * How documents and history are *shown* (plan §2.6).
 *
 * The photo-set indicator is the only thing on the tab that uses colour, and it uses exactly two tones:
 * `success` for a slot that is filled and `muted` for one that is not. Nothing here is a warning — the
 * old lifecycle's photo gate was removed with the lifecycle (plan §3.1), so a missing photograph is a
 * fact worth showing and never a refusal.
 */

/** The five availability photos, in the order the prototype's grid shows them (PP §8). */
export const REQUIRED_PHOTO_CATEGORIES: readonly DocCategory[] = ['PHOTO_FRONT', 'PHOTO_REAR', 'PHOTO_LEFT', 'PHOTO_RIGHT', 'PHOTO_INTERIOR'];

/** What the "Reports & invoices" table lists: everything that is not a photograph or the licence. */
export const REPORT_CATEGORIES: readonly DocCategory[] = ['EXTERNAL_REPORT', 'INVOICE', 'OTHER'];

/** Every category a person may file a report under, for the upload control and the meta sheet. */
export const DOC_CATEGORIES: readonly DocCategory[] = [
  'PHOTO_FRONT',
  'PHOTO_REAR',
  'PHOTO_LEFT',
  'PHOTO_RIGHT',
  'PHOTO_INTERIOR',
  'PHOTO_DAMAGE',
  'LICENSE',
  'EXTERNAL_REPORT',
  'INVOICE',
  'OTHER',
];

export const SERVICE_CATEGORIES: readonly ServiceCategory[] = [
  'ROUTINE',
  'WEAR_AND_TEAR',
  'MECHANICAL_REPAIR',
  'MAJOR_POWERTRAIN',
  'HYBRID_EV',
  'MINOR_BODY',
  'MAJOR_BODY',
  'SRS_AIRBAG',
  'OTHER',
];

export const photoSlotTone = (isPresent: boolean): StatusTone => (isPresent ? 'success' : 'muted');

export const isPhotoCategory = (category: DocCategory): boolean => category.startsWith('PHOTO_');

/** The `accept` attribute for a slot: a photograph is never a PDF. */
export const acceptFor = (category: DocCategory): string =>
  isPhotoCategory(category) ? 'image/jpeg,image/png' : 'image/jpeg,image/png,application/pdf';
